<template>
  <div class="hidden" aria-hidden="true" />
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  image: {
    type: String,
    default: '',
  },
  canonicalUrl: {
    type: String,
    default: null,
  },
  keywords: {
    type: String,
    default: null,
  },
  isArticle: {
    type: Boolean,
    default: false,
  },
  noIndex: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const siteUrl = 'https://coolliving.io';
const defaultOgImage = `${siteUrl}/images/Coolliving_OG.png`;

const pageTitle = computed(() => {
  const raw = (props.title || '').trim();
  if (!raw) return 'Coolliving';
  if (/(\||\u2014|\u2013)\s*Coolliving\s*$/i.test(raw)) return raw;
  return `${raw} | Coolliving`;
});

const canonicalLink = computed(() => {
  if (props.canonicalUrl) {
    return props.canonicalUrl.startsWith('http')
      ? props.canonicalUrl
      : `${siteUrl}${props.canonicalUrl}`;
  }
  if (typeof window !== 'undefined') {
    const path = route.path.endsWith('/') && route.path !== '/'
      ? route.path.slice(0, -1)
      : route.path;
    return `${siteUrl}${path}`;
  }
  return siteUrl;
});

const metaImage = computed(() => {
  if (props.image) {
    return props.image.startsWith('http') ? props.image : `${siteUrl}${props.image}`;
  }
  return defaultOgImage;
});

const robotsContent = computed(() =>
  props.noIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
);

const headMeta = computed(() => {
  const tags = [
    { name: 'description', content: props.description },
    { name: 'robots', content: robotsContent.value },
    { property: 'og:type', content: props.isArticle ? 'article' : 'website' },
    { property: 'og:site_name', content: 'Coolliving' },
    { property: 'og:locale', content: 'es_ES' },
    { property: 'og:url', content: canonicalLink.value },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:description', content: props.description },
    { property: 'og:image', content: metaImage.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@coolliving' },
    { name: 'twitter:creator', content: '@coolliving' },
    { name: 'twitter:url', content: canonicalLink.value },
    { name: 'twitter:title', content: pageTitle.value },
    { name: 'twitter:description', content: props.description },
    { name: 'twitter:image', content: metaImage.value },
  ];
  if (props.keywords) {
    tags.unshift({ name: 'keywords', content: props.keywords });
  }
  return tags;
});

const headLinks = computed(() => [
  { rel: 'canonical', href: canonicalLink.value },
]);

useHead({
  title: pageTitle,
  meta: headMeta,
  link: headLinks,
});
</script>
